import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import { hubModuleAllowsAdminSettings, hubModuleHasAnyAccess } from "@/lib/subapp-access";
import { useIntegratorMe } from "@/lib/me-query";
import {
  datetimeLocalValueToIso,
  defaultCustomSnoozeLocalValue,
  formatSnoozedUntilLabel,
  isNotificationActivelySnoozed,
  snoozePresetUntilIso,
} from "@/lib/notificationsSnooze";
import { cn, formatTimestamp, relativeTime } from "@/lib/utils";
import { notificationSourceStatusLabel, notificationTopicDisplayLabel } from "@/lib/notificationsInboxDisplay";
import {
  assignNotificationItemOwner,
  claimNotificationItemOwner,
  fetchNotificationTopicOwners,
  HUB_NOTIFICATION_TOPIC_ADMIN_MODULE,
  isHubNotificationTopic,
  notificationsTopicOwnersQueryKey,
  notificationsUnreadCountQueryKey,
  snoozeNotification,
  type NotificationListRow,
} from "@/lib/notificationsApi";

export type NotificationInboxRowProps = {
  row: NotificationListRow;
  onMarkRead?: () => void;
  markReadPending?: boolean;
  onMarkUnread?: () => void;
  markUnreadPending?: boolean;
  onSnoozed?: (result: { eventId: string; unreadCount: number; snoozedUntil: string }) => void;
};

function topicLabel(row: NotificationListRow): string {
  return notificationTopicDisplayLabel(row);
}

const READ_STATE_BUTTON_CLASS = "min-w-[7rem]";
const TAKE_OVER_BUTTON_CLASS = "relative w-[5.75rem] justify-center";
const OWNER_SELECT_CLASS =
  "h-8 w-[11rem] shrink-0 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500";

function ReadStateButton({
  unread,
  onMarkRead,
  onMarkUnread,
  markReadPending,
  markUnreadPending,
}: {
  unread: boolean;
  onMarkRead?: () => void;
  onMarkUnread?: () => void;
  markReadPending?: boolean;
  markUnreadPending?: boolean;
}): JSX.Element | null {
  if (unread) {
    if (onMarkRead == null) {
      return null;
    }
    return (
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className={READ_STATE_BUTTON_CLASS}
        disabled={markReadPending}
        isLoading={markReadPending}
        onClick={onMarkRead}
      >
        Mark read
      </Button>
    );
  }
  if (onMarkUnread == null) {
    return null;
  }
  return (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      className={READ_STATE_BUTTON_CLASS}
      disabled={markUnreadPending}
      isLoading={markUnreadPending}
      onClick={onMarkUnread}
    >
      Mark unread
    </Button>
  );
}

function NotificationSnoozeMenu({
  eventId,
  disabled,
  onSnoozed,
}: {
  eventId: string;
  disabled?: boolean;
  onSnoozed?: (result: { eventId: string; unreadCount: number; snoozedUntil: string }) => void;
}): JSX.Element {
  const { showToast } = useHubToast();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customUntil, setCustomUntil] = useState(defaultCustomSnoozeLocalValue);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (menuRef.current != null && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
        setCustomOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [menuOpen]);

  const snoozeMutation = useMutation({
    mutationFn: (until: string) => snoozeNotification(eventId, until),
    onSuccess: (data) => {
      setMenuOpen(false);
      setCustomOpen(false);
      onSnoozed?.({ eventId, unreadCount: data.unreadCount, snoozedUntil: data.snoozedUntil });
      showToast({ tone: "success", message: "Notification snoozed." });
    },
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : String(error),
      });
    },
  });

  function snoozePreset(until: string): void {
    snoozeMutation.mutate(until);
  }

  function snoozeCustom(): void {
    try {
      snoozeMutation.mutate(datetimeLocalValueToIso(customUntil));
    } catch (error) {
      showToast({
        tone: "error",
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }

  return (
    <div className="relative" ref={menuRef}>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={disabled || snoozeMutation.isPending}
        isLoading={snoozeMutation.isPending}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className="gap-1"
        onClick={() => {
          setMenuOpen((open) => !open);
          setCustomOpen(false);
        }}
      >
        Snooze
        <ChevronDown className="h-4 w-4" aria-hidden />
      </Button>
      {menuOpen ? (
        <div
          role="menu"
          className="absolute left-0 top-full z-20 mt-1 min-w-[12rem] overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
            disabled={snoozeMutation.isPending}
            onClick={() => snoozePreset(snoozePresetUntilIso("tomorrow"))}
          >
            Tomorrow
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
            disabled={snoozeMutation.isPending}
            onClick={() => snoozePreset(snoozePresetUntilIso("next_monday"))}
          >
            Next Monday
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
            disabled={snoozeMutation.isPending}
            onClick={() => setCustomOpen((open) => !open)}
          >
            Pick date and time
          </button>
          {customOpen ? (
            <div className="space-y-2 border-t border-slate-100 px-3 py-2">
              <Label htmlFor={`notification-snooze-${eventId}`} className="text-xs">
                Return after
              </Label>
              <input
                id={`notification-snooze-${eventId}`}
                type="datetime-local"
                className="h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                value={customUntil}
                disabled={snoozeMutation.isPending}
                onChange={(event) => setCustomUntil(event.target.value)}
              />
              <Button
                type="button"
                size="sm"
                variant="primary"
                disabled={snoozeMutation.isPending}
                isLoading={snoozeMutation.isPending}
                onClick={() => snoozeCustom()}
              >
                Snooze until
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function NotificationInboxRow({
  row,
  onMarkRead,
  markReadPending = false,
  onMarkUnread,
  markUnreadPending = false,
  onSnoozed,
}: NotificationInboxRowProps): JSX.Element {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const meQuery = useIntegratorMe();
  const currentUserId = meQuery.data?.user.id ?? null;
  const hubTopic = isHubNotificationTopic(row.topic) ? row.topic : null;

  const canAssign =
    hubTopic != null && hubModuleAllowsAdminSettings(meQuery.data, HUB_NOTIFICATION_TOPIC_ADMIN_MODULE[hubTopic]);

  const isUnassigned = row.ownerUserId == null;

  const canReadTopicOwners =
    hubTopic != null && hubModuleHasAnyAccess(meQuery.data, HUB_NOTIFICATION_TOPIC_ADMIN_MODULE[hubTopic]);

  const topicOwnersQuery = useQuery({
    queryKey: hubTopic != null ? notificationsTopicOwnersQueryKey(hubTopic) : ["notifications", "topic-owners", "none"],
    queryFn: () => fetchNotificationTopicOwners(hubTopic!),
    // Only staff with module access should load owners; reporters get 403 otherwise and the row can blank.
    enabled: hubTopic != null && currentUserId != null && canReadTopicOwners && (canAssign || isUnassigned),
  });

  const canClaim =
    hubTopic != null &&
    currentUserId != null &&
    (topicOwnersQuery.data?.owners.some((owner) => owner.userId === currentUserId) ?? false);
  const isCurrentOwner =
    row.ownerUserId != null && currentUserId != null && row.ownerUserId === currentUserId;
  const canTakeOver =
    hubTopic != null
    && currentUserId != null
    && !isCurrentOwner
    && ((isUnassigned && canClaim) || (!isUnassigned && canAssign));
  const showTakeOverButton =
    hubTopic != null && currentUserId != null && (canAssign || canClaim);

  function invalidateOwnership(): void {
    void queryClient.invalidateQueries({ queryKey: ["notifications", "list"] });
    void queryClient.invalidateQueries({ queryKey: notificationsUnreadCountQueryKey });
  }

  const assignMutation = useMutation({
    mutationFn: (ownerUserId: string) =>
      assignNotificationItemOwner(hubTopic!, row.sourceId, ownerUserId),
    onSuccess: () => {
      invalidateOwnership();
      showToast({ tone: "success", message: "Owner assigned." });
    },
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : String(error),
      });
    },
  });

  const takeOverMutation = useMutation({
    mutationFn: async () => {
      if (isUnassigned) {
        return claimNotificationItemOwner(hubTopic!, row.sourceId);
      }
      return assignNotificationItemOwner(hubTopic!, row.sourceId, currentUserId!);
    },
    onSuccess: () => {
      invalidateOwnership();
      showToast({ tone: "success", message: "You took over ownership." });
    },
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : String(error),
      });
    },
  });

  const busy = assignMutation.isPending || takeOverMutation.isPending;
  const assignOptions = useMemo(() => {
    const owners = topicOwnersQuery.data?.owners ?? [];
    const options = owners.map((owner) => ({
      userId: owner.userId,
      label: owner.displayName?.trim() ? owner.displayName : owner.email,
    }));
    const currentOwnerId = row.ownerUserId?.trim() ?? "";
    if (
      currentOwnerId !== ""
      && !options.some((option) => option.userId === currentOwnerId)
      && row.ownerDisplayName
    ) {
      options.unshift({
        userId: currentOwnerId,
        label: row.ownerDisplayName,
      });
    }
    return options;
  }, [row.ownerDisplayName, row.ownerUserId, topicOwnersQuery.data?.owners]);
  const unread = row.readAt == null;
  const activelySnoozed =
    row.snoozedUntil != null && isNotificationActivelySnoozed(row.snoozedUntil);
  const showUnreadBadge = unread && !activelySnoozed;
  const snoozedLabel =
    activelySnoozed && row.snoozedUntil != null
      ? formatSnoozedUntilLabel(row.snoozedUntil)
      : null;
  const sourceStatusLabel = notificationSourceStatusLabel(row);

  return (
    <li>
      <div
        className={cn(
          "px-4 py-2.5",
          unread && "bg-slate-50/80",
        )}
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <p
              className={cn(
                "text-sm text-slate-900",
                unread ? "font-semibold" : "font-medium",
              )}
            >
              {row.title}
            </p>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-700">
              {topicLabel(row)}
            </span>
            {sourceStatusLabel != null ? (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600">
                {sourceStatusLabel}
              </span>
            ) : null}
            {showUnreadBadge ? (
              <span className="rounded bg-brand-100 px-1.5 py-0.5 text-xs font-medium text-brand-800">
                Unread
              </span>
            ) : null}
            {snoozedLabel != null ? (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900">
                {snoozedLabel}
              </span>
            ) : null}
          </div>
          <span className="shrink-0 text-xs text-slate-500">
            {relativeTime(row.createdAt)}
            <span className="sr-only"> ({formatTimestamp(row.createdAt)})</span>
          </span>
        </div>

        {hubTopic != null ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {canAssign ? (
              <select
                aria-label={`Reassign ${topicLabel(row)} owner`}
                className={OWNER_SELECT_CLASS}
                value={row.ownerUserId ?? ""}
                disabled={busy || topicOwnersQuery.isLoading}
                onChange={(event) => {
                  const value = event.target.value.trim();
                  if (value === "") {
                    return;
                  }
                  assignMutation.mutate(value);
                }}
              >
                <option value="">Unassigned</option>
                {assignOptions.map((option) => (
                  <option key={option.userId} value={option.userId}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : null}
            {showTakeOverButton ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className={TAKE_OVER_BUTTON_CLASS}
                disabled={
                  !canTakeOver
                  || isCurrentOwner
                  || busy
                  || (meQuery.isLoading && currentUserId == null)
                }
                onClick={() => takeOverMutation.mutate()}
              >
                {takeOverMutation.isPending ? (
                  <span
                    className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-slate-500 border-t-transparent"
                    aria-hidden
                  />
                ) : (
                  "Take over"
                )}
              </Button>
            ) : null}
            <ReadStateButton
              unread={unread}
              onMarkRead={onMarkRead}
              onMarkUnread={onMarkUnread}
              markReadPending={markReadPending}
              markUnreadPending={markUnreadPending}
            />
            <NotificationSnoozeMenu eventId={row.eventId} disabled={busy} onSnoozed={onSnoozed} />
            {row.destinationPath ? (
              <Link
                to={row.destinationPath}
                className="inline-flex h-8 items-center rounded-md border border-slate-200 bg-white px-2.5 text-sm font-medium text-brand-700 hover:bg-slate-50 hover:underline"
              >
                Open related page
              </Link>
            ) : null}
          </div>
        ) : (
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <ReadStateButton
              unread={unread}
              onMarkRead={onMarkRead}
              onMarkUnread={onMarkUnread}
              markReadPending={markReadPending}
              markUnreadPending={markUnreadPending}
            />
            <NotificationSnoozeMenu eventId={row.eventId} onSnoozed={onSnoozed} />
            {row.destinationPath ? (
              <Link
                to={row.destinationPath}
                className="inline-flex h-8 items-center rounded-md border border-slate-200 bg-white px-2.5 text-sm font-medium text-brand-700 hover:bg-slate-50 hover:underline"
              >
                Open related page
              </Link>
            ) : null}
          </div>
        )}
      </div>
    </li>
  );
}
